import { useMemo, useState } from 'react';
import type {
  DailyPurchaseDefault,
  DailyPurchaseGroup,
  Dish,
  ExtraPurchaseItem,
  ExtraPurchaseSelection,
  FixedPurchaseRule,
  IngredientCatalogItem,
  IngredientCategory,
  PurchaseItem,
  WeeklyPlan,
} from '../types';
import {
  dailyPurchaseDefaultAppliesToDate,
  snapshotIngredients,
} from '../lib/calculation';
import {
  DISH_CATEGORY_ORDER,
  dishCategory,
  matchesDishSearch,
} from '../lib/dish-categories';
import {
  PURCHASE_DISPLAY_CATEGORY_OPTIONS,
  ingredientCategoryFromPurchaseDisplay,
  normalizeIngredientCategory,
  purchaseDisplayCategory,
} from '../lib/ingredient-categories';
import { isValidPurchaseQuantity, requiresIntegerQuantity } from '../lib/purchase-quantity';
import { CheckIcon, PlusIcon, SearchIcon, XIcon } from './Icons';
import { BatchAdjustModal } from './BatchAdjustModal';
import { Modal } from './Modal';
import { QuantityAdjustModal } from './QuantityAdjustModal';

const UNITS = ['斤', '两', '公斤', '克', '只', '个', '包', '袋', '瓶', '桶', '箱', '板', '把', '件', '升', '毫升'];

interface Props {
  plan: WeeklyPlan;
  dishes: Dish[];
  ingredients: IngredientCatalogItem[];
  dailyPurchaseDefaults?: DailyPurchaseDefault[];
  dailyPurchaseGroups?: DailyPurchaseGroup[];
  extraPurchaseItems?: PurchaseItem[];
  weeklyDefaultDishIds?: string[];
  fixedPurchaseRules?: FixedPurchaseRule[];
  onOpenFixedSettings?: () => void;
  onOpenWeekFixedAdjustments?: () => void;
  onChange: (plan: WeeklyPlan) => void;
  onOverride?: (key: string, quantity: string) => void;
  onResetOverride?: (key: string) => void;
}

function selectionName(selection: ExtraPurchaseSelection, dishes: Dish[]): string {
  return selection.recipeSnapshot?.dishName
    ?? dishes.find((dish) => dish.id === selection.dishId)?.name
    ?? '已停用菜品';
}

function ingredientSummary(selection: ExtraPurchaseSelection, dishes: Dish[]): string {
  const ingredients = selection.recipeSnapshot?.ingredients
    ?? dishes.find((dish) => dish.id === selection.dishId)?.ingredients
    ?? [];
  return ingredients.map((item) => `${item.name} ${item.quantity}${item.unit}`).join('、');
}

function itemKey(item: Pick<ExtraPurchaseItem, 'name' | 'unit'>): string {
  return `${item.name.trim()}\u0000${item.unit.trim()}`;
}

const WEEKDAY_NAMES = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];

function purchaseRuleLabel(weekdayMask = 127): string {
  if (weekdayMask === 127) return '每个供餐日';
  const weekdays = WEEKDAY_NAMES.filter((_, index) => (weekdayMask & (1 << index)) !== 0);
  return weekdays.length > 0 ? `${weekdays.join('、')}（供餐时）` : '当前没有采购日';
}

function dailyRuleStatus(
  item: DailyPurchaseDefault,
  groups: DailyPurchaseGroup[],
): string {
  const matchingItems = groups.flatMap((group) => {
    if (!dailyPurchaseDefaultAppliesToDate(item, group.date)) return [];
    const purchaseItem = group.items.find((candidate) => (
      candidate.name === item.name
      && candidate.unit === item.unit
      && (
        !candidate.sources?.length
        || candidate.sources.some((source) => source.kind === 'daily_default')
      )
    ));
    return purchaseItem ? [purchaseItem] : [];
  });
  if (matchingItems.length === 0) return '';
  const adjustedCount = matchingItems.filter((candidate) => (
    candidate.finalQuantity !== candidate.calculatedQuantity
  )).length;
  return adjustedCount > 0
    ? `本周 ${matchingItems.length} 天，${adjustedCount} 天已调整`
    : `本周 ${matchingItems.length} 天已自动加入`;
}

function selectionFinalSummary(
  selection: ExtraPurchaseSelection,
  items: PurchaseItem[],
): { text: string; adjusted: boolean; adjustableItem?: PurchaseItem } | null {
  const matchingItems = items.filter((item) => item.sources?.some((source) => (
    source.kind === 'extra' && source.dishId === selection.dishId
  )));
  if (matchingItems.length === 0) return null;
  return {
    text: matchingItems
      .map((item) => `${item.name} ${item.finalQuantity}${item.unit}`)
      .join('、'),
    adjusted: matchingItems.some((item) => item.finalQuantity !== item.calculatedQuantity),
    adjustableItem: matchingItems.length === 1 ? matchingItems[0] : undefined,
  };
}

export function WeeklyExtraPurchases({
  plan,
  dishes,
  ingredients,
  dailyPurchaseDefaults = [],
  dailyPurchaseGroups = [],
  extraPurchaseItems = [],
  weeklyDefaultDishIds = [],
  fixedPurchaseRules = [],
  onOpenFixedSettings,
  onOpenWeekFixedAdjustments,
  onChange,
  onOverride,
  onResetOverride,
}: Props) {
  const selections = plan.extraSelections ?? [];
  const extraItems = plan.extraItems ?? [];
  const [dishPickerOpen, setDishPickerOpen] = useState(false);
  const [ingredientPickerOpen, setIngredientPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dishCategoryFilter, setDishCategoryFilter] = useState('全部');
  const [draftIds, setDraftIds] = useState<Set<string>>(new Set());
  const [batchEditor, setBatchEditor] = useState<ExtraPurchaseSelection | null>(null);
  const [quantityItem, setQuantityItem] = useState<PurchaseItem | null>(null);
  const [itemDraft, setItemDraft] = useState<ExtraPurchaseItem | null>(null);
  const [itemError, setItemError] = useState('');

  const selectedDishIds = useMemo(
    () => new Set(selections.map((selection) => selection.dishId)),
    [selections],
  );
  const weeklyDefaultDishIdSet = useMemo(
    () => new Set(weeklyDefaultDishIds),
    [weeklyDefaultDishIds],
  );
  const activeFixedRuleCount = fixedPurchaseRules.filter((rule) => rule.active).length;
  const resolvedFixedCount = new Set([
    ...(plan.resolvedFixedPurchases?.daily ?? []).map((item) => item.ruleId),
    ...(plan.resolvedFixedPurchases?.weekly ?? []).map((item) => item.ruleId),
  ]).size;
  const usesResolvedFixedPurchases = (plan.fixedPurchaseRulesRevision ?? 0) > 0
    && Boolean(plan.resolvedFixedPurchases);
  const resolvedDailyRuleGroups = useMemo(() => {
    const occurrences = plan.resolvedFixedPurchases?.daily ?? [];
    const byRule = new Map<string, typeof occurrences>();
    for (const occurrence of occurrences) {
      byRule.set(occurrence.ruleId, [...(byRule.get(occurrence.ruleId) ?? []), occurrence]);
    }
    return [...byRule.values()].map((rows) => ({ sample: rows[0], occurrences: rows }));
  }, [plan.resolvedFixedPurchases?.daily]);
  const resolvedDailySkippedRows = useMemo(() => {
    const skipped = plan.resolvedFixedPurchases?.skipped ?? [];
    const byRule = new Map<string, typeof skipped>();
    for (const item of skipped.filter((candidate) => Boolean(candidate.date))) {
      byRule.set(item.ruleId, [...(byRule.get(item.ruleId) ?? []), item]);
    }
    return [...byRule.values()].map((rows) => ({ sample: rows[0], rows }));
  }, [plan.resolvedFixedPurchases?.skipped]);
  // Bootstrap returns the rule versions used as defaults for future weeks.
  // Only use them as a name/status fallback when this plan has no resolved or
  // skipped occurrence at all; never surface their future quantity as if it
  // belonged to the historical/current week being viewed.
  const unresolvedDailyRuleFallback = resolvedDailyRuleGroups.length === 0
    && resolvedDailySkippedRows.length === 0
    ? fixedPurchaseRules.filter((rule) => rule.active && rule.scheduleType !== 'weekly')
    : [];
  const resolvedWeeklyPurchases = plan.resolvedFixedPurchases?.weekly ?? [];
  const weeklyFixedSelections = useMemo(
    () => selections.filter((selection) => weeklyDefaultDishIdSet.has(selection.dishId)),
    [selections, weeklyDefaultDishIdSet],
  );
  const manualSelections = useMemo(
    () => selections.filter((selection) => !weeklyDefaultDishIdSet.has(selection.dishId)),
    [selections, weeklyDefaultDishIdSet],
  );
  const availableDishes = useMemo(() => dishes.filter((dish) => (
    dish.active
    && !selectedDishIds.has(dish.id)
    && (dishCategoryFilter === '全部' || dishCategory(dish) === dishCategoryFilter)
    && matchesDishSearch(dish, query)
  )), [dishCategoryFilter, dishes, query, selectedDishIds]);

  const availableIngredients = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('zh-CN');
    return ingredients.filter((item) => !normalizedQuery
      || `${item.name} ${item.unit} ${item.ingredientCategory} ${purchaseDisplayCategory(item.ingredientCategory, item.name)}`.toLocaleLowerCase('zh-CN').includes(normalizedQuery));
  }, [ingredients, query]);

  const change = (patch: Partial<WeeklyPlan>) => {
    onChange({ ...plan, ...patch, status: 'draft' });
  };

  const openDishPicker = () => {
    setQuery('');
    setDishCategoryFilter('全部');
    setDraftIds(new Set());
    setDishPickerOpen(true);
  };

  const openIngredientPicker = () => {
    setQuery('');
    setIngredientPickerOpen(true);
  };

  const openCustomEditor = (name = '') => {
    setItemError('');
    setItemDraft({
      id: `draft_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      sourceType: 'custom',
      name,
      quantity: '',
      unit: '斤',
      ingredientCategory: normalizeIngredientCategory(undefined, name),
    });
  };

  const chooseIngredient = (ingredient: IngredientCatalogItem) => {
    setIngredientPickerOpen(false);
    setItemError('');
    const existing = extraItems.find((item) => (
      item.sourceType === 'ingredient' && item.ingredientId === ingredient.id
    ));
    setItemDraft(existing ? { ...existing } : {
      id: `draft_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      sourceType: 'ingredient',
      ingredientId: ingredient.id,
      name: ingredient.name,
      quantity: '',
      unit: ingredient.unit,
      ingredientCategory: ingredient.ingredientCategory,
    });
  };

  const saveItem = () => {
    if (!itemDraft) return;
    const normalized: ExtraPurchaseItem = {
      ...itemDraft,
      name: itemDraft.name.trim(),
      quantity: itemDraft.quantity.trim(),
      unit: itemDraft.unit.trim(),
      ingredientCategory: normalizeIngredientCategory(
        itemDraft.ingredientCategory,
        itemDraft.name,
      ),
    };
    if (!normalized.name) {
      setItemError('请输入食材名称');
      return;
    }
    if (!normalized.unit) {
      setItemError('请输入计量单位');
      return;
    }
    if (!isValidPurchaseQuantity(normalized.quantity, normalized.unit)) {
      setItemError(requiresIntegerQuantity(normalized.unit)
        ? `${normalized.unit}为计数单位，请填写大于 0 的整数`
        : '采购数量必须大于 0');
      return;
    }
    const editingIndex = extraItems.findIndex((item) => item.id === normalized.id);
    const duplicate = extraItems.find((item, index) => (
      index !== editingIndex && itemKey(item) === itemKey(normalized)
    ));
    if (duplicate) {
      setItemError(`本周已经直接添加了${duplicate.name} ${duplicate.quantity}${duplicate.unit}，请修改已有项目`);
      return;
    }
    const next = editingIndex >= 0
      ? extraItems.map((item, index) => index === editingIndex ? normalized : item)
      : [...extraItems, normalized];
    change({ extraItems: next });
    setItemDraft(null);
    setItemError('');
  };

  const removeItem = (item: ExtraPurchaseItem) => {
    if (!window.confirm(`确认从本周额外采购中删除“${item.name}”吗？`)) return;
    change({ extraItems: extraItems.filter((candidate) => candidate.id !== item.id) });
  };

  const toggleDishDraft = (dishId: string) => {
    setDraftIds((current) => {
      const next = new Set(current);
      if (next.has(dishId)) next.delete(dishId);
      else next.add(dishId);
      return next;
    });
  };

  const addDishDrafts = () => {
    const additions = dishes.flatMap((dish): ExtraPurchaseSelection[] => {
      if (!draftIds.has(dish.id) || !dish.active || selectedDishIds.has(dish.id)) return [];
      return [{
        dishId: dish.id,
        batchCount: 1,
        recipeSnapshot: { dishName: dish.name, ingredients: snapshotIngredients(dish) },
      }];
    });
    if (additions.length > 0) change({ extraSelections: [...selections, ...additions] });
    setDishPickerOpen(false);
  };

  const removeSelection = (dishId: string) => {
    const name = selectionName(
      selections.find((selection) => selection.dishId === dishId)!,
      dishes,
    );
    if (!window.confirm(`确认从本周额外采购中删除“${name}”吗？`)) return;
    change({ extraSelections: selections.filter((selection) => selection.dishId !== dishId) });
  };

  const setBatchCount = (dishId: string, batchCount: number) => {
    const safe = Math.min(99, Math.max(1, Math.round(batchCount)));
    change({
      extraSelections: selections.map((selection) => selection.dishId === dishId
        ? { ...selection, batchCount: safe }
        : selection),
    });
  };

  const renderDishSelections = (
    rows: ExtraPurchaseSelection[],
    sourceLabel: string,
    fixed = false,
    showActions = true,
  ) => (
    <div className="extra-purchase-list" role="list">
      {rows.map((selection) => {
        const name = selectionName(selection, dishes);
        const finalSummary = fixed ? selectionFinalSummary(selection, extraPurchaseItems) : null;
        const summary = finalSummary?.text ?? ingredientSummary(selection, dishes);
        return (
          <article className="extra-purchase-row" key={selection.id ?? selection.dishId} role="listitem">
            <span className="extra-purchase-row__copy">
              <small>{sourceLabel}</small>
              <strong>{name}</strong>
              {summary && <small>{summary}</small>}
              {finalSummary?.adjusted && <small>本周数量已调整</small>}
            </span>
            {showActions && <div className="extra-purchase-row__actions">
              {fixed && finalSummary?.adjustableItem && onOverride ? (
                <button
                  className="button button--soft button--small extra-purchase-row__quantity-button"
                  type="button"
                  aria-label={`调整${name}采购数量`}
                  onClick={() => setQuantityItem(finalSummary.adjustableItem ?? null)}
                >
                  调整数量
                </button>
              ) : (
                <button
                  className="batch-row__batch extra-purchase-row__batch"
                  type="button"
                  aria-label={`调整${name}批次数量`}
                  onClick={() => setBatchEditor(selection)}
                >
                  {fixed ? '修改批次' : `${selection.batchCount}批 · 修改`}
                </button>
              )}
              <button
                className="button button--ghost button--small extra-purchase-delete"
                type="button"
                aria-label={fixed ? `${name}本周不买` : `删除${name}`}
                onClick={() => removeSelection(selection.dishId)}
              >
                {fixed ? '本周不买' : '删除'}
              </button>
            </div>}
          </article>
        );
      })}
    </div>
  );

  return (
    <section className="weekly-extra-purchases" aria-labelledby="weekly-extra-purchases-title">
      <header className="section-heading weekly-extra-purchases__heading">
        <span className="step-badge">2</span>
        <div>
          <h2 id="weekly-extra-purchases-title">固定与额外采购</h2>
          <p>系统会自动加入固定项目，缺什么再从食材或菜品中添加。</p>
        </div>
      </header>

      <div className="weekly-extra-purchases__card">
        {(onOpenWeekFixedAdjustments || onOpenFixedSettings) && (
          <div className="fixed-purchase-overview-actions" data-qa="fixed-purchase-overview-actions" aria-label="固定采购操作">
            {onOpenWeekFixedAdjustments && (
              <button type="button" onClick={onOpenWeekFixedAdjustments}>
                <span><strong>调整本周固定采购</strong><small>只改{plan.weekStart.slice(5).replace('-', '月')}日这周，不影响以后</small></span>
                <em>{resolvedFixedCount || activeFixedRuleCount} 项</em>
              </button>
            )}
            {onOpenFixedSettings && (
              <button type="button" onClick={onOpenFixedSettings}>
                <span><strong>管理长期固定规则</strong><small>新增、修改或停用以后每周的规则</small></span>
                <em>{activeFixedRuleCount} 项</em>
              </button>
            )}
          </div>
        )}
        <div className="extra-purchase-groups">
          {(usesResolvedFixedPurchases
            ? resolvedDailyRuleGroups.length + resolvedDailySkippedRows.length + unresolvedDailyRuleFallback.length > 0
            : dailyPurchaseDefaults.length > 0) && (
            <section className="extra-purchase-group" aria-labelledby="daily-fixed-purchase-title">
              <header>
                <strong id="daily-fixed-purchase-title">每日固定采购</strong>
                <span>按供餐日自动加入</span>
              </header>
              <div className="extra-purchase-list" role="list">
                {usesResolvedFixedPurchases ? <>
                  {resolvedDailyRuleGroups.map(({ sample, occurrences }) => {
                  const adjustedCount = occurrences.filter((item) => item.adjusted).length;
                  return (
                    <article className="extra-purchase-row extra-purchase-row--item" key={sample.ruleId} role="listitem">
                      <span className="extra-purchase-row__copy">
                        <small>{sample.scheduleType === 'service_day' ? '每个供餐日' : purchaseRuleLabel(sample.weekdayMask)}</small>
                        <span className="extra-purchase-row__equal">
                          <strong>{sample.name}</strong>
                          <strong>{sample.quantity ?? sample.batchCount ?? '—'}<em>{sample.quantity !== undefined ? sample.unit : '批'}</em></strong>
                        </span>
                        <small>{occurrences.length > 0 ? `本周 ${occurrences.length} 天已自动加入${adjustedCount ? `，${adjustedCount} 天已调整` : ''}` : '本周没有符合供餐条件的日期'}</small>
                      </span>
                    </article>
                  );
                  })}
                  {resolvedDailySkippedRows.map(({ sample, rows }) => (
                    <article className="extra-purchase-row extra-purchase-row--item" key={sample.ruleId} role="listitem">
                      <span className="extra-purchase-row__copy">
                        <small>按日期固定规则</small>
                        <span className="extra-purchase-row__equal">
                          <strong>{sample.name}</strong>
                          <strong>本周未加入</strong>
                        </span>
                        <small>{rows.some((item) => item.reason === 'week_override') ? '本周已手动取消' : '本周没有符合供餐条件的日期'}</small>
                      </span>
                    </article>
                  ))}
                  {unresolvedDailyRuleFallback.map((rule) => (
                    <article className="extra-purchase-row extra-purchase-row--item" key={rule.id} role="listitem">
                      <span className="extra-purchase-row__copy">
                        <small>{rule.scheduleType === 'service_day' ? '每个供餐日' : purchaseRuleLabel(rule.weekdayMask)}</small>
                        <span className="extra-purchase-row__equal">
                          <strong>{rule.name}</strong>
                          <strong>本周未加入</strong>
                        </span>
                        <small>当前周没有已解析的采购日期</small>
                      </span>
                    </article>
                  ))}
                </> : dailyPurchaseDefaults.map((item, index) => {
                  const status = dailyRuleStatus(item, dailyPurchaseGroups);
                  return (
                    <article
                      className="extra-purchase-row extra-purchase-row--item"
                      key={item.id ?? `${itemKey(item)}-${index}`}
                      role="listitem"
                    >
                      <span className="extra-purchase-row__copy">
                        <small>{purchaseRuleLabel(item.weekdayMask)}</small>
                        <span className="extra-purchase-row__equal">
                          <strong>{item.name}</strong>
                          <strong>{item.quantity}<em>{item.unit}</em></strong>
                        </span>
                        {status && <small>{status}</small>}
                      </span>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {(usesResolvedFixedPurchases ? resolvedWeeklyPurchases.length > 0 : weeklyFixedSelections.length > 0) && (
            <section className="extra-purchase-group" aria-labelledby="weekly-fixed-purchase-title">
              <header>
                <strong id="weekly-fixed-purchase-title">每周固定采购</strong>
                <span>已自动加入 · {usesResolvedFixedPurchases ? resolvedWeeklyPurchases.length : weeklyFixedSelections.length} 项</span>
              </header>
              {usesResolvedFixedPurchases ? (
                <div className="extra-purchase-list" role="list">
                  {resolvedWeeklyPurchases.map((item) => (
                    <article className="extra-purchase-row extra-purchase-row--item" key={item.ruleId} role="listitem">
                      <span className="extra-purchase-row__copy">
                        <small>每周固定</small>
                        <span className="extra-purchase-row__equal">
                          <strong>{item.name}</strong>
                          <strong>{item.quantity ?? item.batchCount ?? '—'}<em>{item.quantity !== undefined ? item.unit : '批'}</em></strong>
                        </span>
                        {item.adjusted && <small>本周数量已调整</small>}
                      </span>
                    </article>
                  ))}
                </div>
              ) : renderDishSelections(weeklyFixedSelections, '每周固定', true, true)}
            </section>
          )}

          <section className="extra-purchase-group" aria-labelledby="manual-extra-purchase-title">
            <header>
              <strong id="manual-extra-purchase-title">本周另加</strong>
              <span>{extraItems.length + manualSelections.length} 项</span>
            </header>
            {extraItems.length === 0 && manualSelections.length === 0 ? (
              <div className="weekly-extra-purchases__empty">
                <strong>本周没有另外添加</strong>
                <p>需要临时补货时，可直接加食材或按菜品加入完整配方。</p>
              </div>
            ) : (
              <>
                {extraItems.length > 0 && (
                  <div className="extra-purchase-list" role="list" aria-label="本周直接采购">
                    {extraItems.map((item, index) => (
                      <article className="extra-purchase-row extra-purchase-row--item" key={item.id ?? `${itemKey(item)}-${index}`} role="listitem">
                        <span className="extra-purchase-row__copy">
                          <small>{item.sourceType === 'ingredient' ? '食材库' : '本周自定义'} · {purchaseDisplayCategory(item.ingredientCategory, item.name)}</small>
                          <span className="extra-purchase-row__equal">
                            <strong>{item.name}</strong>
                            <strong>{item.quantity}<em>{item.unit}</em></strong>
                          </span>
                        </span>
                        <div className="extra-purchase-row__actions">
                          <button className="button button--soft button--small" type="button" onClick={() => { setItemError(''); setItemDraft({ ...item }); }}>
                            修改
                          </button>
                          <button className="button button--ghost button--small extra-purchase-delete" type="button" onClick={() => removeItem(item)}>
                            删除
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
                {manualSelections.length > 0 && renderDishSelections(manualSelections, '按菜品采购')}
              </>
            )}
          </section>
        </div>

        <div className="extra-purchase-actions" aria-label="额外采购添加方式">
          <button type="button" onClick={openIngredientPicker}>
            <PlusIcon size={20} /><span><strong>选食材</strong><small>从食材库直接采购</small></span>
          </button>
          <button type="button" onClick={openDishPicker}>
            <PlusIcon size={20} /><span><strong>按菜品添加</strong><small>自动带出完整配方</small></span>
          </button>
          <button type="button" onClick={() => openCustomEditor()}>
            <PlusIcon size={20} /><span><strong>自定义添加</strong><small>食材库中没有时使用</small></span>
          </button>
        </div>
      </div>

      <Modal open={dishPickerOpen} title="按菜品添加" eyebrow="可选择任意分类的菜品" size="large" onClose={() => setDishPickerOpen(false)}>
        <div className="extra-purchase-picker">
          <div className="dish-selector__search extra-purchase-picker__search">
            <SearchIcon size={19} />
            <input aria-label="搜索额外采购菜品" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索菜品或食材" />
            {query && <button type="button" onClick={() => setQuery('')} aria-label="清空搜索"><XIcon size={16} /></button>}
          </div>
          <div className="extra-purchase-picker__categories" aria-label="筛选菜品分类">
            {['全部', ...DISH_CATEGORY_ORDER].map((category) => (
              <button type="button" className={dishCategoryFilter === category ? 'is-active' : ''} onClick={() => setDishCategoryFilter(category)} key={category}>
                {category}
              </button>
            ))}
          </div>
          {availableDishes.length > 0 ? (
            <div className="extra-purchase-picker__list" role="list" aria-label="可添加的菜品">
              {availableDishes.map((dish) => {
                const selected = draftIds.has(dish.id);
                return (
                  <article key={dish.id} role="listitem">
                    <button className={`extra-purchase-option ${selected ? 'is-selected' : ''}`} type="button" aria-pressed={selected} onClick={() => toggleDishDraft(dish.id)}>
                      <span className="extra-purchase-option__check">{selected && <CheckIcon size={17} />}</span>
                      <span><strong>{dish.name}</strong><small>{dishCategory(dish)} · {dish.ingredients.map((item) => `${item.name} ${item.quantity}${item.unit}`).join('、')}</small></span>
                    </button>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="extra-purchase-picker__empty"><strong>没有找到可添加的菜品</strong><p>换个关键词或分类试试。</p></div>
          )}
          <footer className="extra-purchase-picker__actions">
            <button className="button button--ghost" type="button" onClick={() => setDishPickerOpen(false)}>取消</button>
            <button className="button button--primary" type="button" disabled={draftIds.size === 0} onClick={addDishDrafts}>加入本周（{draftIds.size}）</button>
          </footer>
        </div>
      </Modal>

      <Modal open={ingredientPickerOpen} title="选择食材" eyebrow="选择后填写本周采购数量" size="large" onClose={() => setIngredientPickerOpen(false)}>
        <div className="extra-purchase-picker extra-purchase-picker--ingredients">
          <div className="dish-selector__search extra-purchase-picker__search">
            <SearchIcon size={19} />
            <input aria-label="搜索已有食材" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索食材名称" />
            {query && <button type="button" onClick={() => setQuery('')} aria-label="清空搜索"><XIcon size={16} /></button>}
          </div>
          {availableIngredients.length > 0 ? (
            <div className="extra-purchase-picker__list" role="list" aria-label="已有食材">
              {availableIngredients.map((ingredient) => {
                const existing = extraItems.some((item) => item.sourceType === 'ingredient' && item.ingredientId === ingredient.id);
                return (
                  <article key={ingredient.id} role="listitem">
                    <button className={`extra-purchase-option extra-purchase-option--ingredient ${existing ? 'is-selected' : ''}`} type="button" onClick={() => chooseIngredient(ingredient)}>
                      <span className="extra-purchase-option__check">{existing && <CheckIcon size={17} />}</span>
                      <span><strong>{ingredient.name}</strong><small>{purchaseDisplayCategory(ingredient.ingredientCategory, ingredient.name)} · 单位：{ingredient.unit}{existing ? ' · 已加入，点击修改' : ''}</small></span>
                    </button>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="extra-purchase-picker__empty">
              <strong>食材库里没有找到</strong>
              <p>可以把“{query.trim()}”作为自定义食材加入本周。</p>
              <button className="button button--primary" type="button" onClick={() => { setIngredientPickerOpen(false); openCustomEditor(query.trim()); }}>自定义添加</button>
            </div>
          )}
          <footer className="extra-purchase-picker__actions extra-purchase-picker__actions--single">
            <button className="button button--ghost" type="button" onClick={() => setIngredientPickerOpen(false)}>取消</button>
          </footer>
        </div>
      </Modal>

      <Modal
        open={Boolean(itemDraft)}
        title={itemDraft?.sourceType === 'ingredient' ? `采购${itemDraft.name}` : itemDraft?.name ? `编辑${itemDraft.name}` : '自定义添加'}
        eyebrow="只影响本周，不写入菜品库"
        onClose={() => { setItemDraft(null); setItemError(''); }}
      >
        {itemDraft && (
          <div className="extra-item-form">
            <label className="field">
              <span>食材名称</span>
              <input
                value={itemDraft.name}
                readOnly={itemDraft.sourceType === 'ingredient'}
                onChange={(event) => setItemDraft({ ...itemDraft, name: event.target.value })}
                placeholder="例如：食盐"
              />
            </label>
            <div className="extra-item-form__quantity">
              <label className="field">
                <span>采购数量</span>
                <input
                  autoFocus={false}
                  type="number"
                  inputMode={requiresIntegerQuantity(itemDraft.unit) ? 'numeric' : 'decimal'}
                  min={requiresIntegerQuantity(itemDraft.unit) ? '1' : '0.001'}
                  step={requiresIntegerQuantity(itemDraft.unit) ? '1' : 'any'}
                  value={itemDraft.quantity}
                  onChange={(event) => setItemDraft({ ...itemDraft, quantity: event.target.value })}
                  placeholder="请输入数量"
                />
              </label>
              <label className="field">
                <span>计量单位</span>
                <input
                  list="extra-purchase-units"
                  value={itemDraft.unit}
                  readOnly={itemDraft.sourceType === 'ingredient'}
                  onChange={(event) => setItemDraft({ ...itemDraft, unit: event.target.value })}
                  placeholder="单位"
                />
              </label>
            </div>
            <label className="field">
              <span>食材类别</span>
              <select
                value={purchaseDisplayCategory(itemDraft.ingredientCategory, itemDraft.name)}
                disabled={itemDraft.sourceType === 'ingredient'}
                onChange={(event) => setItemDraft({ ...itemDraft, ingredientCategory: ingredientCategoryFromPurchaseDisplay(event.target.value as ReturnType<typeof purchaseDisplayCategory>) })}
              >
                {PURCHASE_DISPLAY_CATEGORY_OPTIONS.map((category) => <option value={category} key={category}>{category}</option>)}
              </select>
            </label>
            <datalist id="extra-purchase-units">{UNITS.map((unit) => <option value={unit} key={unit} />)}</datalist>
            {itemError && <p className="form-error" role="alert">{itemError}</p>}
            <div className="form-actions">
              <button className="button button--ghost" type="button" onClick={() => { setItemDraft(null); setItemError(''); }}>取消</button>
              <button className="button button--primary" type="button" onClick={saveItem}>加入本周</button>
            </div>
          </div>
        )}
      </Modal>

      {batchEditor && (
        <BatchAdjustModal
          open
          dishName={selectionName(batchEditor, dishes)}
          value={batchEditor.batchCount}
          onClose={() => setBatchEditor(null)}
          onApply={(value) => setBatchCount(batchEditor.dishId, value)}
        />
      )}
      <QuantityAdjustModal
        item={quantityItem}
        onClose={() => setQuantityItem(null)}
        onApply={onOverride ?? (() => {})}
        onReset={onResetOverride}
      />
    </section>
  );
}
